import React from 'react';
import { useLocation } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../../hooks';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Study, UpdateRequest } from '../../../models';
import { EntityStatus, EntityState } from 'adc-sirius-module-common';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';
import { enumToChoiceOptions } from '../../../utilities/helpers';
import { QrCodePopup } from '../../qrCode/QrCodePopup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  studyQueryOptions,
  updateStudyMutateOptions,
} from '../../../queryOptions';
import { studyIdSchema } from 'adc-sirius-module-common';

type StudyHeaderProps = {
  study: Study;
};

type StudyFormValues = Omit<Study, 'status'> &
  UpdateRequest & {
    status: string; // converted to string for dropdown input
  };

/**
 * StudyHeader component displays the header information for a specific study.
 * @param {Study} study - The study object containing details to display.
 * @returns {React.JSX.Element}
 */
export default function StudyHeader({ study }: StudyHeaderProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const updateBreadcrumb = (label) => {
    navigateWithBreadcrumbs(location.pathname, label, label, {
      replace: true,
    });
  };

  const mapFormValuesToStudy = (values: Partial<StudyFormValues>): Study => {
    const { newUpdateReason, ...updatedValues } = values;
    const updatedStudy = {
      ...study,
      ...updatedValues,
      status: Number(updatedValues.status ?? study.status), // convert status back to number for API
      updateReason: newUpdateReason as string,
    };

    return updatedStudy;
  };

  const mapStudyToFormValues = (study: Study): StudyFormValues => ({
    ...study,
    status: String(study.status),
    newUpdateReason: '',
  });

  const formConfig: DynamicFormConfig<StudyFormValues> = {
    fields: [
      {
        name: 'name',
        label: i18n.t('Page_Study.Form.name'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'description',
        label: i18n.t('Page_Study.Form.description'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'status',
        label: i18n.t('Page_Study.Form.status'),
        type: DynamicFormFieldType.CHOICE,
        isEditable: true,
        choiceOptions: enumToChoiceOptions(EntityStatus, 'Status', {
          sortBy: 'name',
        }),
      },
      {
        name: 'region',
        label: i18n.t('Page_Study.Form.region'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'type',
        label: i18n.t('Page_Study.Form.type'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Study.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Study.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Study.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Study.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Study.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Study.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string().required(i18n.t('Form.genericRequired')),
      name: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .matches(
          studyIdSchema.matcher,
          i18n.t('Form.allowedSpecialCharacters', {
            characters: studyIdSchema.allowedSpecialCharacters.join(' '),
          }),
        )
        .test(
          'len',
          () =>
            i18n.t('Form.genericMaxLength', { count: studyIdSchema.maxLength }),
          (val) =>
            studyIdSchema.maxLength && val
              ? val.length < studyIdSchema.maxLength
              : true,
        ),
      description: Yup.string(),
      organizationKey: Yup.string(),
      status: Yup.string().required(i18n.t('Form.genericRequired')),
      region: Yup.string(),
      createdBy: Yup.string().required(i18n.t('Form.genericRequired')),
      createdOn: Yup.string().required(i18n.t('Form.genericRequired')),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      state: Yup.mixed()
        .oneOf(Object.values(EntityState))
        .required(i18n.t('Form.genericRequired')),
      type: Yup.string(),
    },
    initialValues: mapStudyToFormValues(study),
  };

  const updateStudy = useMutation(updateStudyMutateOptions());

  const handleSubmit = async (values: Partial<StudyFormValues>) => {
    const studyValues = mapFormValuesToStudy(values);
    const updatedStudy = await updateStudy.mutateAsync({
      studyKey: study.key,
      updatedStudyValues: studyValues,
    });

    const studyQueryKey = studyQueryOptions({ key: updatedStudy.key }).queryKey;

    queryClient.setQueryData(studyQueryKey, updatedStudy);

    updateBreadcrumb(updatedStudy.name);

    const updatedFormValues = mapStudyToFormValues(updatedStudy);

    return updatedFormValues;
  };

  return (
    <VStack flexGrow={1} gap={'$4'}>
      <Card>
        <DynamicForm
          id="study-details"
          title={i18n.t('Page_Study.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
        >
          <QrCodePopup studyIdentifier={study.name} studyKey={study.key} />
        </DynamicForm>
      </Card>
    </VStack>
  );
}
